import { Box, Button, Card, Container, Typography } from "@mui/material"
import { ReduxState, useSafeSetState } from "ra-core"
import { FC, useEffect } from "react"
import { useSelector } from "react-redux"
import { useLocation } from "react-router-dom"
import LoadingScreen from "~/components/LoadingScreen"
import { SupportedResources } from "~/dataProvider/tools"
import { SaveActionSlim } from "~/dataProvider/types"
import { EmailValidateInput, UserInvitationResponseDto } from "~/gen/api/user"
import { OdResourcePageProps } from "~/types/resource"
import { parse } from "~/utils/queryStringWrapper"
import LayoutPlain from "./LayoutPlain"
import { Helmet } from 'react-helmet-async'
import InvitationConfirmForm from "~/components/authentication/InvitationConfirmForm"
import { useResourceRegister } from "~/utils/resourceRegister"
import retrieveAxiosErr from "~/utils/retrieveAxiosErr"
import ClientError from "../ClientError"
import { Link as RouterLink } from 'react-router-dom'
import CreateOrEdit from "~/controller/CreateOrEdit"
import { useSimpleSaveAction } from "~/controller/SaveContextProvider"


const resource: SupportedResources = 'user-invitation-validate'

const InvitationConfirm: FC = (props) => {
  useResourceRegister({ name: resource })
  useResourceRegister({ name: 'autologin' })

  const validationResult = useSelector<ReduxState, any>(store => store.admin.resources[resource]?.data[-1])
  return (
    <CreateOrEdit 
      resource={resource} basePath="" mutationMode={'pessimistic'} 
      id={-1} 
      record={validationResult}
    >
      <InvitationConfirmCater {...props} />
    </CreateOrEdit>
  )
}

const InvitationConfirmCater: FC<OdResourcePageProps> = (props) => {
  const save = useSimpleSaveAction()
  const location = useLocation()

  const [ states, setStates ] = useSafeSetState({
    code: null as string|null,
    cid: null as string|null,
    error: null as any,
    validationSent: false,
    validationResult: undefined as UserInvitationResponseDto|undefined,
  })

  useEffect(() => {
    if (!location.search) { return }
    setStates(v => {
      if (v.code) { return v }

      const queries = parse(location.search)
      const get = (x:string) => {
        const entry = queries[x]
        return !entry ? 'never'
        : entry instanceof Array ? entry[0]
        : entry
      }
      const code = get('code')
      const cid = get('cid')
      if (code) {
        const sendValidate: SaveActionSlim<EmailValidateInput> = save
        const payload = { code, cid } as EmailValidateInput
        sendValidate(payload, {
          onSuccess: ({data}: {data:UserInvitationResponseDto}) => {
            setStates(v1 => ({ ...v1, validationResult: data, validationSent: true }))
          },
          onFailure: (err) => {
            setStates(v1 => ({ ...v1, error: err.message, validationSent: true }))
          },
        })
      }

      return { ...v, code, cid }
    })
  }, [location.search, save, setStates])

  
  if (!states.validationSent) {
    return <LoadingScreen />
  }
  if (states.error) {
    const errMsg = retrieveAxiosErr(states.error)
    const errNum = states.error.body?.status || 400
    return (
      <LayoutPlain>
        <ClientError 
          errorNumber={errNum}
          errorMsg={errMsg}
          shortTitle="Invitation Confirm"
          escapeEntry={
            <Button
              color="primary"
              component={RouterLink}
              to="/login"
              variant="outlined"
            >
              Take me to Sign In properly
            </Button>
          }
        />
      </LayoutPlain>
    )
  }

  return (
    <LayoutPlain>
      <Helmet>
        <title>Invitation Confirm | OnData</title>
      </Helmet>
      <Typography
        color="textPrimary"
        sx={{ 
          fontWeight: 'fontWeightBold',
          textAlign: 'center',
          pb: 3,
        }}
        variant="h5"
      >
        Welcome! You have been invited to join an OnData account.
      </Typography>
      <Typography
        color="textSecondary"
        variant="body2"
        sx={{ textAlign: 'center', pb: 4 }}
      >
        {states.validationResult?.inviteeName} ({states.validationResult?.inviteeEmail}) has invited you to join OnData.
        {!states.validationResult || states.validationResult.isSsoEnabled !== false ? null : ' Please choose a password for your account.'}
      </Typography>

      <Card
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            lg: 'repeat(2, 1fr)',
            xs: 'repeat(1, 1fr)'
          },
          minHeight: '100%'
        }}
      >
        <Box sx={{ pt: 8 }} >
          <Container
            maxWidth="md"
            sx={{
              pl: {
                lg: 15
              }
            }}
          >
            {states.validationResult ?
              (states.validationResult.isSsoEnabled ?
                <>
                  <Typography
                    sx={{ color: 'primary.main', pb: 3 }}
                    variant="h6"
                  >
                    Greetings from OnData!
                  </Typography>
                  <Typography
                    color="textPrimary"
                    sx={{ py: 0.5 }}
                    variant="body1"
                  >
                    Your organization has Single Sign On turned on, we will redirect you to your Identity Provider shortly after your OnData account is ready.
                  </Typography>
                </> : 

                <>
                  <Typography
                    sx={{ color: 'primary.main', pb: 3 }}
                    variant="h6"
                  >
                    Password Guidelines
                  </Typography>
                  <Typography
                    color="textPrimary"
                    sx={{ py: 0.5 }}
                    variant="body1"
                  >
                    1. Password must be eight or more characters long.
                  </Typography>
                  <Typography
                    color="textPrimary"
                    sx={{ py: 0.5 }}
                    variant="body1"
                  >
                    2. Password must contain uppercase and lowercase characters, numbers and special characters.
                  </Typography>
                </>) : null
            }
          </Container>
        </Box>
        <Box
          sx={{
            backgroundColor: 'background.paper',
            pt: 8,
            pb: 3,
          }}
        >
          <Container
            maxWidth="md"
            sx={{
              pr: {
                lg: 8
              }
            }}
          >
            <Box sx={{}}>
              <InvitationConfirmForm 
                email={states.validationResult?.email || ''}
                code={states.code || ''}
                cid={states.cid || ''}
                isSsoEnabled={states.validationResult?.isSsoEnabled || false}
              />
            </Box>
          </Container>
        </Box>
      </Card>
    
    </LayoutPlain>
  )
}

export default InvitationConfirm;
